import { nextTick } from 'vue'
import { instance as store } from 'app/store'
import { hydrate } from 'app/store/local'
import find from 'lodash/find'
import get from 'lodash/get'
import once from 'lodash/once'
import debounce from 'lodash/debounce'
import { update } from 'app/metaSocial'
import { safeNextPathOrNull } from 'app/utils/safeNextPath'

function anyTrue(to, prop) {
  return find([...to.matched].map((t) => t.meta[prop]))
}

function isSupportEnabled() {
  return store.getters['permits/has']('humanSupport')
}

export const ensureLocalsLoaded = (vueInstance) => async (to, from, next) => {
  await hydrate(store)

  next()
}

export const ensureSession = (vueInstance) => async (to, from, next) => {
  const isSignedIn = store.getters['sessions/isSignedIn']
  const account = store.getters['sessions/account']

  if (!isSignedIn) {
    try {
      await store.dispatch('sessions/fetchCurrentSession')

      to.meta.isSignedIn = true
      next()
    } catch (e) {
      to.meta.isSignedIn = false
      next()
    }
  } else {
    to.meta.isSignedIn = isSignedIn
    to.meta.account = account
    next()
  }
}

export const ensureSignedOut = (vueInstance) => async (to, from, next) => {
  const { isSignedIn } = to.meta
  const denyAuth = anyTrue(to, 'denyAuth')

  if (isSignedIn && denyAuth) {
    try {
      await store.dispatch('sessions/signOut')
    } finally {
      next()
    }
  } else {
    next()
  }
}

export const ensureSignedIn = (vueInstance) => async (to, from, next) => {
  const requiresAdmin = anyTrue(to, 'requiresAdmin')
  const requiresAuth = requiresAdmin ? true : anyTrue(to, 'requiresAuth')

  if (requiresAuth) {
    const { isSignedIn } = to.meta

    if (isSignedIn) {
      if (requiresAdmin) {
        const isAdmin = store.getters['accounts/isAdmin']

        if (isAdmin) {
          next()
        } else {
          next({ path: '/sessions/sign-in', query: { next: to.fullPath } })
        }
      } else {
        next()
      }
    } else {
      next({ path: '/sessions/sign-in', query: { next: to.fullPath } })
    }
  } else {
    next()
  }
}

const scrollTo = (to) => {
  function _s() {
    const target = to.hash.substr(1)
    let element = null

    // If the scroll changes before the initial attempt the user is either
    // interacting with the page or a saved scroll position is being used.
    // In this case we need to abort, we don't want to jack with something
    // the user has done by themselves.
    if (window.scrollY !== 0) {
      return false
    }

    if (target) {
      element = document.getElementById(target)
    }

    if (element && typeof element.scrollIntoViewIfNeeded === 'function') {
      element.scrollIntoViewIfNeeded()
      return true
    } else if (element && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView()
      return true
    } else {
      return false
    }
  }

  // This will first attempt to scroll directly to the element on the page if
  // it's available. However, in a lot of cases the content is waiting on an
  // API call or some other delayed action and the Element ID may not be
  // available yet. In this case we detect the failure (returns false) and then
  // schedule another go at it a half a second later.
  return function () {
    if (!_s()) {
      setTimeout(_s, 500)
    }
  }
}

// `nextTick` is a named export in Vue 3, not a method on the instance. This
// took `vueInstance.nextTick(...)`, and bootstrap passes the app object, which
// has no such method — so the first hash navigation threw.
export const initialScrollWithHash = () =>
  once((to) => {
    nextTick(scrollTo(to))
  })

export const initializeBeacon = (vueInstance) => () => {
  // disable help scout on search to improve load time
  if (isSupportEnabled() && window.Beacon && window.location.pathname !== '/search') {
    window.Beacon('config', { display: { style: 'iconAndText', text: 'Help' } })
  }
}

const fetchMetaSocialDebounced = debounce(
  (fullPath) => {
    store.dispatch('metaSocial/fetch', fullPath).then(update)
  },
  3000,
  { leading: true }
)

export const metaSocialLoad = (vueInstance) => (to, from, next) => {
  if (from.matched.length > 0) {
    fetchMetaSocialDebounced(to.fullPath)
  }
  next()
}

// Child routes of the same parent keep the parent view mounted, so switching
// between them works like a tab change rather than a new page.
const isSameParentView = (to, from) => to.matched.length > 1 && from.matched.length > 1 && to.matched[0] === from.matched[0]

export const focusMainContent = () => (to, from) => {
  // Skip initial load — browser handles default focus.
  if (!from.name && from.matched.length === 0) return

  // Skip when the page didn't change: hash or query-only updates (in-page
  // anchors, search state written to the URL) and child-route tab switches.
  // Moving focus there pulls it out of the control the user is working in.
  if (to.path === from.path || isSameParentView(to, from)) return

  nextTick(() => {
    const main = document.getElementById('main-content')
    if (!main) return

    if (!main.hasAttribute('tabindex')) {
      main.setAttribute('tabindex', '-1')
    }

    // preventScroll so router's scrollBehavior stays authoritative.
    main.focus({ preventScroll: true })
  })
}

// We are disabling this feature until we are able to get a better fix in.
export const loadDiscount = (vueInstance) => async (to, from, next) => {
  const discount = get(to, ['query', 'discount'], null)

  if (discount) {
    store.dispatch('discounts/setDiscount', discount)
  }
  next()
}

const CHUNK_RELOAD_KEY = 'fa-stale-chunk-reload'

// webpack names its failure ChunkLoadError ("Loading chunk 123 failed"); a CSS
// chunk fails as "Loading CSS chunk …"; a native dynamic import fails as
// "Failed to fetch dynamically imported module".
const isChunkLoadError = (error) =>
  error?.name === 'ChunkLoadError' || /Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module/.test(error?.message || '')

// Production chunk files are content-hashed, so after a deploy a tab that is
// still running the previous build asks for chunks the new build replaced.
// Router 4 rejects that navigation and the click does nothing. Load the page
// fresh instead, which picks up the new build. One reload per target path: if
// the chunk is still missing afterwards, the navigation fails as before rather
// than looping.
//
// The destination goes through safeNextPath: the router does not sanitize, so a
// route pushed with a string like `//evil.example/x` would otherwise reach
// location.assign as an off-site URL. Anything that is not a same-origin site
// path is not reloaded at all.
export const reloadOnStaleChunk =
  ({ storage = window.sessionStorage, location = window.location } = {}) =>
  (error, to) => {
    if (!isChunkLoadError(error) || !to) return

    const destination = safeNextPathOrNull(to.fullPath)
    if (!destination) return

    try {
      if (storage.getItem(CHUNK_RELOAD_KEY) === destination) return
      storage.setItem(CHUNK_RELOAD_KEY, destination)
    } catch (e) {
      // No storage (private mode, blocked): reload anyway; a second failure on
      // the same path then just fails, which is no worse than today.
    }

    location.assign(destination)
  }
